/**
 * NotificationService wrapper for Sonner
 * 
 * Centralizes toast/notification calls to:
 * 1. Prevent scattered direct imports across the codebase
 * 2. Enable future enhancements (analytics, notification taxonomy)
 * 3. Enforce Sonner-only policy (shadcn Toast is deprecated)
 * 
 * @see https://ui.shadcn.com/docs/components/sonner
 */

import { toast as sonnerToast, type ExternalToast } from 'sonner'

type ToastOptions = ExternalToast

/**
 * Show a success toast (ephemeral confirmation)
 */
export function success(message: string, options?: ToastOptions) {
  return sonnerToast.success(message, options)
}

/**
 * Show an error toast
 * Note: For critical errors requiring action, use a blocking error panel instead
 */
export function error(message: string, options?: ToastOptions) {
  return sonnerToast.error(message, options)
}

/**
 * Show an info toast
 */
export function info(message: string, options?: ToastOptions) {
  return sonnerToast.info(message, options)
}

/**
 * Show a warning toast
 * Note: For persistent warnings, use a non-blocking banner instead
 */
export function warning(message: string, options?: ToastOptions) {
  return sonnerToast.warning(message, options)
}

/**
 * Show a loading toast (returns dismiss function)
 */
export function loading(message: string, options?: ToastOptions) {
  return sonnerToast.loading(message, options)
}

/**
 * Show a promise toast (loading → success/error)
 */
export function promise<T>(
  promiseArg: Promise<T>,
  messages: {
    loading: string
    success: string | ((data: T) => string)
    error: string | ((error: unknown) => string)
  }
) {
  return sonnerToast.promise(promiseArg, messages)
}

/**
 * Dismiss a specific toast or all toasts
 */
export function dismiss(toastId?: string | number) {
  return sonnerToast.dismiss(toastId)
}

/**
 * Generic toast (for custom rendering)
 */
export function custom(message: string, options?: ToastOptions) {
  return sonnerToast(message, options)
}

// Create a callable toast function with methods for backward compatibility
// This allows both toast('message') and toast.success('message') patterns
type ToastFunction = {
  (message: string, options?: ToastOptions): string | number
  success: typeof success
  error: typeof error
  info: typeof info
  warning: typeof warning
  loading: typeof loading
  promise: typeof promise
  dismiss: typeof dismiss
  custom: typeof custom
}

// Base callable function (info style by default)
const toast: ToastFunction = Object.assign(
  (message: string, options?: ToastOptions) => sonnerToast(message, options),
  {
    success,
    error,
    info,
    warning,
    loading,
    promise,
    dismiss,
    custom,
  }
)

export default toast
